import React, { useState } from "react";
import { Button } from "flowbite-react";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { SiJira, SiAsana, SiMicrosoftazure, SiMicrosoftsharepoint } from "react-icons/si";
import { Drawer } from "../../../components/Modals";
import ImportProjectModal from "../../../components/Modals/ImportProjectModal";

interface ImportDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const ImportDrawer = ({ isOpen, onClose }: ImportDrawerProps) => {
  const [selectedImportSource, setSelectedImportSource] = useState<string>("");
  const [showAzureModal, setShowAzureModal] = useState<boolean>(false);

  const handleImportSourceClick = (source: string) => {
    setSelectedImportSource(source);
    
    // If the source is Azure DevOps, open the ImportProjectModal directly
    if (source === "devopsAsure") {
      setShowAzureModal(true);
      onClose(); // Close the drawer when opening Azure modal
    } else {
      // For other sources, just close the drawer
      onClose();
    }
  };

  const handleCloseDrawer = () => {
    setSelectedImportSource("");
    onClose();
  };

  const handleCloseAzureModal = () => {
    setShowAzureModal(false);
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={handleCloseDrawer}
        position="right"
        className="w-full sm:w-[480px]"
      >
        <div className="relative h-[calc(100%-55px)] overflow-auto flex flex-col">
          <button 
            type="button" 
            className="ml-auto absolute right-[10px] top-2 dark:text-white text-gray-700 w-8 h-8 rounded-full bg-black text-sm z-[999] flex items-center justify-center hover:opacity-80" 
            onClick={handleCloseDrawer}
          > 
            X 
          </button>
          
          <div className="px-6 py-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Import Projects
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Choose a source to import projects from
            </p>
            
            {/* Integration credentials message */}
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start">
              <HiOutlineInformationCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5 mr-3" />
              <p className="text-sm text-blue-700 dark:text-blue-300">
                If you don't see your integration here, please go to Settings and add your credentials to activate the integration.
              </p>
            </div>
            
            <div className="grid grid-cols-1 gap-4 mt-4">
              <div 
                className={`bg-white dark:bg-slate-800 rounded-lg border p-4 flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${selectedImportSource === "jira" ? "border-blue-500 ring-2 ring-blue-200 dark:ring-blue-900" : "border-gray-200 dark:border-gray-700"}`}
                onClick={() => handleImportSourceClick("jira")}
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-blue-50 dark:bg-blue-900/30 rounded-full">
                  <SiJira className="h-7 w-7 text-blue-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-md font-medium text-gray-900 dark:text-white">Jira</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Connect your Jira account</p>
                </div>
              </div>
              
              <div 
                className={`bg-white dark:bg-slate-800 rounded-lg border p-4 flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${selectedImportSource === "asana" ? "border-red-500 ring-2 ring-red-200 dark:ring-red-900" : "border-gray-200 dark:border-gray-700"}`}
                onClick={() => handleImportSourceClick("asana")}
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-red-50 dark:bg-red-900/30 rounded-full">
                  <SiAsana className="h-7 w-7 text-red-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-md font-medium text-gray-900 dark:text-white">Asana</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Connect your Asana account</p>
                </div>
              </div>
              
              <div 
                className={`bg-white dark:bg-slate-800 rounded-lg border p-4 flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${selectedImportSource === "devopsAsure" ? "border-blue-600 ring-2 ring-blue-200 dark:ring-blue-900" : "border-gray-200 dark:border-gray-700"}`}
                onClick={() => handleImportSourceClick("devopsAsure")}
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-blue-50 dark:bg-blue-900/30 rounded-full">
                  <SiMicrosoftazure className="h-7 w-7 text-blue-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-md font-medium text-gray-900 dark:text-white">Azure DevOps</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Connect your Azure DevOps account</p>
                </div>
              </div>
              
              <div 
                className={`bg-white dark:bg-slate-800 rounded-lg border p-4 flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors ${selectedImportSource === "monday" ? "border-yellow-500 ring-2 ring-yellow-200 dark:ring-yellow-900" : "border-gray-200 dark:border-gray-700"}`}
                onClick={() => handleImportSourceClick("monday")}
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-yellow-50 dark:bg-yellow-900/30 rounded-full">
                  <svg viewBox="0 0 24 24" width="26" height="26" fill="#ff3d57" className="h-7 w-7">
                    <path d="M9.45 6a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zm8.55 0a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zM3.45 15.9a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zM12 15.9a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zm8.55 0a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zm-14.55 9a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zm8.55 0a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-md font-medium text-gray-900 dark:text-white">Monday</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Connect your Monday account</p>
                </div>
              </div>
            </div>
            
            {/* Removed the buttons for Cancel and Import */}
          </div>
        </div>
      </Drawer>

      {/* Azure Import Modal */}
      {showAzureModal && (
        <ImportProjectModal
          showModal={showAzureModal}
          setShowModal={setShowAzureModal}
          onImportSuccess={() => {
            setShowAzureModal(false);
            handleCloseDrawer();
          }}
        />
      )}
    </>
  );
};

export default ImportDrawer; 